import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer, TableRow, Slider } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { updateCalculations, setAdditionalPayment } from '../../store/presentationSlice';
import { mockCreditReport } from '../../utils/mockData';
import {
  calculateMonthlyInterest,
  calculateAnnualInterest,
  calculatePaymentBreakdown,
  calculatePayoffTime,
  calculateScenario,
  ANNUAL_INTEREST_RATE
} from '../../utils/calculations';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { colors } from '../../theme/colors';
import SlideHeader from './SlideHeader';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const InterestCalculatorSlide: React.FC = () => {
  const dispatch = useDispatch();
  const { calculations, additionalPayment } = useSelector((state: RootState) => state.presentation);
  const totalBalance = 42000; // Set to exact balance
  const monthlyPayment = 1260; // Minimum payment

  useEffect(() => {
    const monthlyInterest = 651; // Exact interest amount
    const annualInterest = monthlyInterest * 12;
    const breakdown = {
      interestPayment: monthlyInterest,
      principalPayment: monthlyPayment - monthlyInterest
    };
    
    dispatch(updateCalculations({
      monthlyInterest,
      annualInterest,
      paymentBreakdown: breakdown
    }));
  }, [dispatch]);

  const paymentBreakdown = {
    interestPayment: 651,
    principalPayment: monthlyPayment - 651
  };

  const pieData = {
    labels: ['Principal Payment', 'Interest Payment'],
    datasets: [
      {
        data: [paymentBreakdown.principalPayment, paymentBreakdown.interestPayment],
        backgroundColor: [
          'rgba(75, 192, 75, 0.8)', // Green for principal
          'rgba(220, 53, 69, 0.9)', // More intense red for interest
        ],
        borderColor: [
          'rgba(75, 192, 75, 1)',
          'rgba(220, 53, 69, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Monthly Payment Breakdown',
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const value = context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: $${value.toFixed(2)} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 14
        },
        formatter: (value: number, ctx: any) => {
          const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${percentage}%`;
        }
      }
    },
    maintainAspectRatio: false,
  };

  const payoffTime = calculatePayoffTime(
    totalBalance,
    monthlyPayment,
    paymentBreakdown.interestPayment,
    ANNUAL_INTEREST_RATE
  );

  const scenario = calculateScenario(
    totalBalance,
    monthlyPayment,
    paymentBreakdown.interestPayment,
    additionalPayment,
    ANNUAL_INTEREST_RATE
  );

  const handleAdditionalPaymentChange = (event: Event, value: number | number[]) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    dispatch(setAdditionalPayment(newValue));
  };

  return (
    <Box>
      <SlideHeader title="Interest Calculator" />
      {/* Content Section */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle1" fontWeight="bold">
                          Current Average Interest Rate
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="error">
                          {ANNUAL_INTEREST_RATE}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle1" fontWeight="bold">
                          Monthly Interest Payment
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="error">
                          ${paymentBreakdown.interestPayment.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle1" fontWeight="bold">
                          Monthly Principal Payment
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="#28A745">
                          ${paymentBreakdown.principalPayment.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total Monthly Payment
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">
                          ${monthlyPayment.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body1">
                  Of your ${monthlyPayment.toFixed(2)} monthly payment, ${calculations.monthlyInterest.toFixed(2)} ({((paymentBreakdown.interestPayment / monthlyPayment) * 100).toFixed(1)}%) 
                  goes to interest alone. That's ${calculations.annualInterest.toFixed(2)} per year not reducing your ${totalBalance.toLocaleString()} balance.
                </Typography>

                <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                  By eliminating interest, your full payment would go towards debt reduction, accelerating your path to freedom.
                </Typography>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Paper sx={{ p: 3, width: '100%' }}>
              <Box sx={{ width: '100%', height: '350px' }}>
                <Pie data={pieData} options={pieOptions} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={4} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: '#0A1929',
                mb: 2,
                textAlign: 'center'
              }}>
                Projected Payoff Time
              </Typography>
              {payoffTime.willNeverPayoff ? (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="error">
                    Warning: At your current payment amount, this debt will never be paid off
                    because the interest charges are higher than your monthly payment.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      At your current rate, it will take approximately:
                    </Typography>
                    <Typography variant="h4" color="error" sx={{ mb: 0.5 }}>
                      {payoffTime.years} years
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({payoffTime.months} months)
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      alignItems: 'center'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 3,
                        width: '100%'
                      }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Original Balance
                          </Typography>
                          <Typography variant="h5" color="#0A1929" sx={{ fontWeight: 600 }}>
                            ${totalBalance.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Total Interest Paid
                          </Typography>
                          <Typography variant="h5" color="error" sx={{ fontWeight: 600 }}>
                            ${payoffTime.totalInterestPaid.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Total amount you'll pay:
                        </Typography>
                        <Typography variant="h3" color="error" sx={{ 
                          fontWeight: 600
                        }}>
                          ${payoffTime.totalPaid.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: '#0A1929',
                mb: 2,
                textAlign: 'center'
              }}>
                What If You Paid More?
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom sx={{ textAlign: 'center' }}>
                  Additional monthly payment: ${additionalPayment}
                </Typography>
                <Slider
                  value={additionalPayment}
                  onChange={handleAdditionalPaymentChange}
                  aria-labelledby="additional-payment-slider"
                  valueLabelDisplay="auto"
                  step={50}
                  marks
                  min={0}
                  max={1000}
                  sx={{
                    width: '100%',
                    '& .MuiSlider-thumb': {
                      height: 24,
                      width: 24,
                      backgroundColor: '#fff',
                      border: '2px solid currentColor',
                    }
                  }}
                />
              </Box>
              {!scenario.newPayoff.willNeverPayoff && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1">
                    By paying an extra ${additionalPayment}/month:
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    • New payoff time: <strong>{scenario.newPayoff.years} years</strong>
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    • You would save: ${Math.round(scenario.savings).toLocaleString()}
                  </Typography>
                  <Typography variant="body1">
                    • Time saved: {Math.round(scenario.timeSavedMonths / 12)} years and {scenario.timeSavedMonths % 12} months
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default InterestCalculatorSlide;
